import type { Subscription } from '@rails/actioncable';
import {
  getV1CompaniesCompanyIdOrders,
  getV1CompaniesCompanyIdOrdersId,
  getV1CompaniesCompanyIdOrdersOrderIdPhasesTypeahead,
  Order_Intake,
  Order_Intake_Create,
  Order_Read,
  OrderIntakeChannel_Failure_Read,
  OrderIntakeChannel_Read,
  OrderIntakeChannel_Success_Read,
  patchV1CompaniesCompanyIdOrdersId,
  postV1CompaniesCompanyIdOrders,
  postV1CompaniesCompanyIdOrdersOrderIdCopy,
  putV1CompaniesCompanyIdOrdersOrderIdAccept,
  putV1CompaniesCompanyIdOrdersOrderIdDriverSms,
  putV1CompaniesCompanyIdOrdersOrderIdHold,
  putV1CompaniesCompanyIdOrdersOrderIdRequest,
  putV1CompaniesCompanyIdOrdersOrderIdStart,
  putV1CompaniesCompanyIdOrdersOrderIdWeatherHold,
  UnitOfDistance,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useRef } from 'react';
import { v4 as UUID } from 'uuid';

import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { Order, OrderIntakeDraft } from '~hooks/useOrders/models';
import { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink, PaginationQuery } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { PhaseBasic } from '../useProjects/models';

interface GetOrdersByCompanyIdProps {
  companyId: string;
  link?: PaginationLink;
  searchQuery?: string;
  filterParams?: Record<string, PaginationQuery[string]>;
}

interface GetOrderPhasesTypeaheadProps {
  companyId?: string;
  orderId: string;
}

interface OrderIntakeSubscription extends Subscription {
  intake(data: Order_Intake): void;
}

interface IntakeOrderResult {
  metadata?: Order_Intake_Create['metadata'];
  order: OrderIntakeDraft;
}

const parseIntakeOrder = (data: Order_Intake_Create): IntakeOrderResult | undefined => {
  if (!data.order) {
    return;
  }

  return {
    metadata: data.metadata,
    order: OrderIntakeDraft.parse(data.order),
  };
};

export const useOrders = () => {
  const { ordersStore, userStore } = useStores();
  const orderSubscriptionRef = useRef<Subscription>();

  const { addSearchHeaderParam } = useDataGridSearch();
  const companyId = getEffectiveUserCompanyId(userStore);

  const subscribeToOrderUpdates = () => {
    if (!companyId) {
      return;
    }

    const consumer = connection.realTimeConnection;
    const subscription = consumer?.subscriptions?.create?.(
      {
        channel: realTimeChannels.CompanyOrderUpdateChannel,
        company_id: companyId,
      },
      {
        connected: () => () => {},
        disconnected: () => () => {},
        received: (resp: { data: Order_Read }) => {
          const order = Order.parse(resp?.data);

          if (userStore.isCurrentCompanyActive) {
            if (!ordersStore.ordersPagination.after) {
              ordersStore.addOrder(order);
            } else {
              ordersStore.updateOrder(order);
            }
          } else {
            if (!ordersStore.childCompanyOrdersPagination.after) {
              ordersStore.addChildCompanyOrder(order);
            } else {
              ordersStore.updateChildCompanyOrder(order);
            }
          }
        },
      },
    );

    if (!subscription) {
      return;
    }

    orderSubscriptionRef.current?.unsubscribe();
    orderSubscriptionRef.current = subscription;

    return subscription;
  };

  const intakeOrder = async (data: Order_Intake) => {
    try {
      const consumer = connection.realTimeConnection;
      if (!consumer?.subscriptions?.create) {
        throw new Error('No subscription available');
      }

      return await new Promise<IntakeOrderResult>((resolve, reject) => {
        let isSettled = false;
        const intakeSubscription: { current?: OrderIntakeSubscription } = {};

        const settlePromise = (action: () => void) => {
          if (isSettled) {
            return;
          }

          isSettled = true;
          action();
          intakeSubscription.current?.unsubscribe();
        };

        intakeSubscription.current = consumer.subscriptions.create(
          {
            channel: realTimeChannels.OrderIntakeChannel,
            company_id: companyId,
            id: UUID(),
          },
          {
            connected() {
              (this as OrderIntakeSubscription).perform('intake', data);
            },
            disconnected: () => {
              settlePromise(() => {
                reject(new Error('Order intake connection was lost'));
              });
            },
            received: (resp: OrderIntakeChannel_Read) => {
              if (resp.status === 'processing') {
                return;
              }

              if (resp.status === 'error') {
                const error = resp as OrderIntakeChannel_Failure_Read;
                settlePromise(() => {
                  reject(new Error(error.message || 'Order intake failed'));
                });
                return;
              }

              const success = resp as OrderIntakeChannel_Success_Read;
              if (!success.data) {
                settlePromise(() => {
                  reject(new Error('No data returned from order intake'));
                });
                return;
              }

              const parsedOrder = parseIntakeOrder(success.data);
              if (!parsedOrder) {
                settlePromise(() => {
                  reject(new Error('No order returned from order intake'));
                });
                return;
              }

              settlePromise(() => {
                resolve(parsedOrder);
              });
            },
            intake(intakeData: Order_Intake) {
              this.perform('intake', intakeData);
            },
          },
        ) as OrderIntakeSubscription | undefined;

        if (!intakeSubscription.current) {
          settlePromise(() => {
            reject(new Error('No subscription available'));
          });
        }
      });
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_create'));
    }
  };

  const getOrderById = async (id: string, signal?: AbortSignal) => {
    try {
      const response = await getV1CompaniesCompanyIdOrdersId({
        path: { 'company-id': companyId, id },
        signal,
      });
      return Order.parse(response.data.data);
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.orders.failed_to_fetch_order'),
      );
      throw error;
    }
  };

  const getOrdersByCompanyId = async ({
    companyId,
    filterParams,
    link,
    searchQuery,
  }: GetOrdersByCompanyIdProps) => {
    let params: PaginationQuery = {
      'page[limit]': ordersStore.childCompanyOrdersPagination.limit,
    };

    if (link && ordersStore.childCompanyOrdersPagination[link]) {
      params[`page[${link}]`] = ordersStore.childCompanyOrdersPagination[link];
    }

    params = addSearchHeaderParam({
      searchValue: searchQuery,
      filterParams,
      params,
    });

    try {
      const response = await getV1CompaniesCompanyIdOrders({
        path: { 'company-id': companyId },
        query: params,
      });

      const formatted = response.data.data.map(Order.parse);
      const pagination = extractPagination(response);

      ordersStore.setChildCompanyOrders(formatted);
      ordersStore.setChildCompanyOrdersPagination(pagination);
      ordersStore.updateChildCompanyOrdersPageNumber(link);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_fetch'));
    }
  };

  const createOrder = async (order: NewOrderFormSchemaInterface) => {
    try {
      const distanceUnit = userStore.userCompany.isMeters
        ? UnitOfDistance.KILOMETER
        : UnitOfDistance.MILE;
      const response = await postV1CompaniesCompanyIdOrders({
        path: { 'company-id': companyId },
        body: Order.deparse(order, distanceUnit),
      });
      const orderSaved = Order.parse(response.data.data);

      if (userStore.isCurrentCompanyActive) {
        if (!ordersStore.ordersPagination.after) {
          ordersStore.addOrder(orderSaved);
        }
      } else {
        if (!ordersStore.childCompanyOrdersPagination.after) {
          ordersStore.addChildCompanyOrder(orderSaved);
        }
      }

      return orderSaved;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_create'));
    }
  };

  const updateOrder = async (order: NewOrderFormSchemaInterface) => {
    try {
      if (!order.id) {
        throw new Error('Order id is required');
      }

      const distanceUnit = userStore.userCompany.isMeters
        ? UnitOfDistance.KILOMETER
        : UnitOfDistance.MILE;
      const response = await patchV1CompaniesCompanyIdOrdersId({
        path: { 'company-id': companyId, id: order.id },
        body: Order.deparseUpdate(order, distanceUnit),
      });
      const orderSaved = Order.parse(response.data.data);

      if (userStore.isCurrentCompanyActive) {
        ordersStore.updateOrder(orderSaved);
      } else {
        ordersStore.updateChildCompanyOrder(orderSaved);
      }

      return orderSaved;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_update'));
    }
  };

  const callOrderEventEndpoint = async (
    orderId: string,
    fn: typeof putV1CompaniesCompanyIdOrdersOrderIdStart,
    errorMessage: string,
  ) => {
    try {
      const response = await fn({
        path: { 'company-id': companyId, 'order-id': orderId },
        body: {},
      });

      const formatted = Order.parse(response.data.data);
      ordersStore.updateOrder(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(error, errorMessage);
    }
  };

  const acceptOrder = (orderId: string) =>
    callOrderEventEndpoint(
      orderId,
      putV1CompaniesCompanyIdOrdersOrderIdAccept,
      t('error_messages.orders.failed_to_accept'),
    );

  const holdOrder = (orderId: string) =>
    callOrderEventEndpoint(
      orderId,
      putV1CompaniesCompanyIdOrdersOrderIdHold,
      t('error_messages.orders.failed_to_hold'),
    );

  const requestOrder = (orderId: string) =>
    callOrderEventEndpoint(
      orderId,
      putV1CompaniesCompanyIdOrdersOrderIdRequest,
      t('error_messages.orders.failed_to_request'),
    );

  const startOrder = (orderId: string) =>
    callOrderEventEndpoint(
      orderId,
      putV1CompaniesCompanyIdOrdersOrderIdStart,
      t('error_messages.orders.failed_to_start'),
    );

  const weatherHoldOrder = (orderId: string) =>
    callOrderEventEndpoint(
      orderId,
      putV1CompaniesCompanyIdOrdersOrderIdWeatherHold,
      t('error_messages.orders.failed_to_weather_hold'),
    );

  const cloneOrder = async (id: string, includeAssignee: boolean = false) => {
    try {
      const response = await postV1CompaniesCompanyIdOrdersOrderIdCopy({
        path: { 'company-id': companyId, 'order-id': id },
        body: {
          include_assignee: includeAssignee,
        },
      });

      const formatted = Order.parse(response.data.data);

      if (!ordersStore.ordersPagination.after?.length) {
        ordersStore.addOrder(formatted);
      }

      ordersStore.addClonedOrder(formatted);
      return formatted;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.orders.failed_to_duplicate'),
      );
    }
  };

  const sendSmsToAllDrivers = async (orderId: string, message: string) => {
    try {
      await putV1CompaniesCompanyIdOrdersOrderIdDriverSms({
        path: { 'company-id': companyId, 'order-id': orderId },
        body: { message },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.orders.failed_to_send_text'),
      );
    }
  };

  const getOrderPhasesTypeahead = async (options: GetOrderPhasesTypeaheadProps) => {
    try {
      const phases = await fetchAllApiPages(
        (query) =>
          getV1CompaniesCompanyIdOrdersOrderIdPhasesTypeahead({
            path: {
              'company-id': options.companyId ?? companyId,
              'order-id': options.orderId,
            },
            query,
          }),
        PhaseBasic.parse,
      );

      ordersStore.setPhaseTypeheadsByOrderID(options.orderId, phases);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.driver_days.failed_to_fetch_phases'),
      );
    }
  };

  const addNewPhasesToOrder = useCallback(
    async (
      companyId: string,
      orderId: string,
      phases: { name: string; code: string }[],
    ) => {
      try {
        const response = await patchV1CompaniesCompanyIdOrdersId({
          path: { 'company-id': companyId, id: orderId },
          body: { phases },
        });
        const updatedOrder = Order.parse(response.data.data);
        ordersStore.updateOrder(updatedOrder);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.orders.failed_to_add_phase'),
          [422],
        );
      }
    },
    [ordersStore],
  );

  return {
    acceptOrder,
    addNewPhasesToOrder,
    cloneOrder,
    createOrder,
    getOrderById,
    getOrderPhasesTypeahead,
    getOrdersByCompanyId,
    holdOrder,
    intakeOrder,
    requestOrder,
    sendSmsToAllDrivers,
    startOrder,
    subscribeToOrderUpdates,
    updateOrder,
    weatherHoldOrder,
  } as const;
};
